import React, { useEffect, useMemo, useState } from "react";
import { fetchHomeData } from "../api/homeApi";
import { fetchTournaments } from "../api/tournamentApi";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  LogoUsers,
  LogoTrophy,
  LogoTarget,
  LogoRocket,
  LogoBook,
  LogoMedal,
  LogoMail,
  LogoHeart,
} from "../components/CardLogos";
import "../css/Home.css";

const HERO_IMAGE = "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1920&q=80";
const PROGRAM_IMAGES = [
  "https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=600&q=80",
  "https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=600&q=80",
  "https://images.unsplash.com/photo-1531415074968-036ba1b575da?w=600&q=80",
  "https://images.unsplash.com/photo-1541534741688-6078c6bfb5c5?w=600&q=80",
];

const UPCOMING_TOURNAMENTS_LINK = "/tournaments#events";

function getTournamentStatus(tournament) {
  const regStatus = tournament.registration?.status;
  if (regStatus === "Open") return "Registrations open";
  if (regStatus) return regStatus;
  return "Upcoming";
}

function mapUpcomingForCarousel(upcomingTournaments) {
  return (upcomingTournaments || []).map((tournament, index) => ({
    id: tournament.id,
    name: tournament.name,
    sport: tournament.sport || "General",
    status: getTournamentStatus(tournament),
    image: PROGRAM_IMAGES[index % PROGRAM_IMAGES.length],
  }));
}

function getMaxVisibleOffset(total) {
  if (total <= 1) return 0;
  if (total <= 3) return 1;
  return 2;
}

function getCarouselCountClass(total) {
  if (total <= 1) return "htc--single";
  if (total === 2) return "htc--count-2";
  if (total === 3) return "htc--count-3";
  if (total === 4) return "htc--count-4";
  return "htc--count-5plus";
}

function getCardWidth() {
  if (typeof window === "undefined") return 330;
  if (window.innerWidth <= 640) return 285;
  if (window.innerWidth <= 900) return 305;
  return 330;
}

function getWingAngles(maxOffset) {
  if (maxOffset >= 2) {
    /* Outer slots use gentler Y so cards stay face-visible (not edge-on lines). */
    return { "-2": -58, "-1": -52, 0: 0, 1: 52, 2: 58 };
  }
  return { "-1": -62, 0: 0, 1: 62 };
}

function getPetalConfig(level) {
  switch (level) {
    case 0:
      return { peek: 1, rotateYMult: 0, rotateX: 0, translateZ: 12, petalY: -4, scale: 1 };
    case 1:
      return { peek: 0.4, rotateYMult: 1.05, rotateX: 30, translateZ: -72, petalY: 42, scale: 0.86 };
    case 2:
      return { peek: 0.2, rotateYMult: 0.82, rotateX: 34, translateZ: -108, petalY: 62, scale: 0.72 };
    default:
      return { peek: 0.2, rotateYMult: 0.82, rotateX: 34, translateZ: -108, petalY: 62, scale: 0.72 };
  }
}

function lerpPetalConfig(a, b, t) {
  return {
    peek: a.peek + (b.peek - a.peek) * t,
    rotateYMult: a.rotateYMult + (b.rotateYMult - a.rotateYMult) * t,
    rotateX: a.rotateX + (b.rotateX - a.rotateX) * t,
    translateZ: a.translateZ + (b.translateZ - a.translateZ) * t,
    petalY: a.petalY + (b.petalY - a.petalY) * t,
    scale: a.scale + (b.scale - a.scale) * t,
  };
}

function interpolatePetalConfig(absSlot) {
  if (absSlot < 0.35) return getPetalConfig(0);
  if (absSlot < 1.35) {
    const t = (absSlot - 0.35) / 1;
    return lerpPetalConfig(getPetalConfig(0), getPetalConfig(1), t);
  }
  const t = Math.min(1, (absSlot - 1.35) / 0.65);
  return lerpPetalConfig(getPetalConfig(1), getPetalConfig(2), t);
}

/** Push wings outward so only peek fraction faces the center card. */
function getPetalX(slotOffset, absSlot, cardWidth, cfg) {
  const sign = slotOffset >= 0 ? 1 : -1;
  const w = cardWidth * cfg.scale;
  const hidden = w * (1 - cfg.peek);
  const layerBoost = absSlot >= 1.35 ? 34 : 12;
  return sign * (hidden * 0.58 + layerBoost);
}

function interpolateWingAngle(slot, angles, maxOffset) {
  const clamped = Math.max(-maxOffset, Math.min(maxOffset, slot));
  const lo = Math.floor(clamped);
  const hi = Math.ceil(clamped);
  const read = (key) => angles[String(key)] ?? 0;
  if (lo === hi) return read(lo);
  const t = clamped - lo;
  return read(lo) + (read(hi) - read(lo)) * t;
}

function getCardRole(slotOffset, isFront) {
  if (isFront) return "front";
  if (slotOffset <= -1.35) return "left-2";
  if (slotOffset <= -0.35) return "left";
  if (slotOffset >= 1.35) return "right-2";
  if (slotOffset >= 0.35) return "right";
  return slotOffset >= 0 ? "right" : "left";
}

function getAngleStep(total) {
  return 360 / total;
}

/** Shortest step count from one slide index to another (for click-to-slide). */
function getContinuousDelta(from, to, total) {
  let delta = to - from;
  if (delta > total / 2) delta -= total;
  if (delta < -total / 2) delta += total;
  return delta;
}

function normalizeAngle(angleDeg) {
  let norm = ((angleDeg % 360) + 360) % 360;
  if (norm > 180) norm -= 360;
  return norm;
}

/** Visible wing layout by count: 1=center, 2=center+1, 3=center+1 each side, 4+=2 wings per side. */
function getCardMotion(index, rotationDeg, total, cardWidth) {
  const angleStep = getAngleStep(total);
  const norm = normalizeAngle(index * angleStep + rotationDeg);
  const slotOffset = norm / angleStep;
  const absSlot = Math.abs(slotOffset);
  const maxOffset = getMaxVisibleOffset(total);
  const visible = maxOffset === 0 ? absSlot < 0.35 : absSlot <= maxOffset + 0.05;

  const petal = interpolatePetalConfig(absSlot);
  const layoutAngle =
    maxOffset === 0 || absSlot < 0.35
      ? 0
      : interpolateWingAngle(slotOffset, getWingAngles(maxOffset), maxOffset);
  const x = absSlot < 0.35 ? 0 : getPetalX(slotOffset, absSlot, cardWidth, petal);

  const isFront = absSlot < 0.35;
  let rotateY = -layoutAngle * petal.rotateYMult;
  if (absSlot >= 1.35) {
    rotateY = Math.sign(rotateY || slotOffset) * Math.min(Math.abs(rotateY), 46);
  }
  const rotateX = isFront ? 0 : petal.rotateX;
  const translateZ = isFront ? 0 : petal.translateZ;
  const petalY = isFront ? -4 : petal.petalY;
  const scale = isFront ? 1 : petal.scale;
  const opacity = !visible ? 0 : isFront ? 1 : 0.92 - Math.min(absSlot, maxOffset) * 0.06;
  const zIndex = visible ? Math.round(50 - absSlot * 10) : 0;
  const role = getCardRole(slotOffset, isFront);

  const xPos = isFront ? 0 : x;
  const yRot = isFront ? 0 : rotateY;

  return {
    style: {
      transform: `translate(-50%, calc(-50% - 0.35rem)) translate3d(${xPos.toFixed(1)}px, ${petalY.toFixed(1)}px, ${translateZ}px) rotateY(${yRot.toFixed(1)}deg) rotateX(${rotateX.toFixed(1)}deg) scale(${scale.toFixed(3)})`,
      zIndex,
      opacity,
      visibility: visible ? "visible" : "hidden",
      pointerEvents: visible && opacity > 0.35 ? undefined : "none",
    },
    visible,
    isFront,
    role,
  };
}

function TournamentHeroCarousel({ tournaments = [] }) {
  const slides = Array.isArray(tournaments) ? tournaments : [];
  const total = slides.length;
  const angleStep = total > 0 ? getAngleStep(total) : 0;
  const [active, setActive] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [paused, setPaused] = useState(false);
  const [cardWidth, setCardWidth] = useState(() => getCardWidth());
  const activeSlide = slides[active];
  const canNavigate = total > 1;
  const useCompactNav = total > 4;
  const countClass = getCarouselCountClass(total);

  const slideKey = slides.map((s) => s.id).join(",");

  useEffect(() => {
    setActive(0);
    setRotation(0);
  }, [slideKey]);

  useEffect(() => {
    setCardWidth(getCardWidth());
  }, [total]);

  useEffect(() => {
    const onResize = () => setCardWidth(getCardWidth());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [total]);

  useEffect(() => {
    if (!canNavigate || paused) return undefined;
    const timer = setInterval(() => {
      setRotation((r) => r - angleStep);
      setActive((i) => (i + 1) % total);
    }, 5500);
    return () => clearInterval(timer);
  }, [paused, total, angleStep, canNavigate]);

  const advance = (delta) => {
    if (delta === 0) return;
    setRotation((r) => r - delta * angleStep);
    setActive((i) => (i + delta + total) % total);
  };

  const setSlide = (nextIndex) => {
    const target = (nextIndex + total) % total;
    advance(getContinuousDelta(active, target, total));
  };

  const go = (delta) => {
    if (!canNavigate) return;
    advance(delta);
  };

  if (!total) return null;

  return (
    <div
      className={`htc ${countClass}`}
      aria-label="Tournaments"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="htc-rings" aria-hidden="true">
        <span className="htc-ring htc-ring--a" />
        <span className="htc-ring htc-ring--b" />
      </div>

      <div className="htc-stage">
        <div className="htc-orbit-track" aria-hidden="true" />
        {slides.map((tournament, index) => {
          const isActive = index === active;
          const motion = getCardMotion(index, rotation, total, cardWidth);
          return (
            <article
              key={tournament.id}
              className={`htc-card${!motion.visible ? " htc-card--hidden" : motion.role === "front" ? " htc-card--front" : ` htc-card--${motion.role}`}`}
              style={motion.style}
              aria-hidden={!isActive}
            >
              <div className={`htc-card-inner${motion.isFront ? " is-front" : ""}`}>
                <button
                  type="button"
                  className="htc-card-hit"
                  onClick={() => setSlide(index)}
                  tabIndex={isActive ? -1 : 0}
                  aria-label={`Show ${tournament.name}`}
                />
                <div
                  className="htc-card-visual"
                  style={{ backgroundImage: `url(${tournament.image})` }}
                >
                  <div className="htc-card-visual-shade" />
                  <div className="htc-card-shine" aria-hidden="true" />
                  <div className="htc-card-sparks" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </div>
                  <span className="htc-card-index" aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  {!motion.isFront ? (
                    <span className="htc-card-peek-sport">{tournament.sport}</span>
                  ) : null}
                </div>
                <div className={`htc-card-panel${motion.isFront ? " is-visible" : ""}`}>
                  <span className="htc-card-badge">{tournament.status}</span>
                  <h3 className="htc-card-title">{tournament.name}</h3>
                  <Link to={UPCOMING_TOURNAMENTS_LINK} className="htc-card-cta">
                    Browse tournaments
                    <span className="htc-card-cta-arrow" aria-hidden="true">
                      →
                    </span>
                  </Link>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {canNavigate ? (
      <div className="htc-controls">
        <button
          type="button"
          className="htc-nav"
          onClick={() => go(-1)}
          aria-label="Previous tournament"
        >
          ‹
        </button>
        <div
          className={`htc-progress${useCompactNav ? " htc-progress--dots" : ""}`}
          role="tablist"
          aria-label="Choose tournament"
          style={useCompactNav ? undefined : { gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))` }}
        >
          {slides.map((tournament, index) => (
            <button
              key={tournament.id}
              type="button"
              role="tab"
              aria-selected={index === active}
              aria-label={useCompactNav ? `${tournament.name} (${index + 1} of ${total})` : tournament.name}
              className={`htc-progress-seg${index === active ? " is-active" : ""}${useCompactNav ? " htc-progress-seg--dot" : ""}`}
              onClick={() => setSlide(index)}
            >
              {useCompactNav ? (
                <span className="htc-progress-dot" aria-hidden="true" />
              ) : (
                <span className="htc-progress-label">{tournament.sport}</span>
              )}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="htc-nav"
          onClick={() => go(1)}
          aria-label="Next tournament"
        >
          ›
        </button>
      </div>
      ) : null}

      <p className="htc-live" aria-live="polite">
        {activeSlide?.name ?? ""}
      </p>
    </div>
  );
}

function Home() {
  const [data, setData] = useState(null);
  const [upcomingTournaments, setUpcomingTournaments] = useState(null);

  useEffect(() => {
    Promise.all([fetchHomeData(), fetchTournaments()]).then(([homeData, tournamentData]) => {
      setData(homeData);
      setUpcomingTournaments(tournamentData?.upcomingTournaments ?? []);
    });
  }, []);

  const carouselTournaments = useMemo(
    () => mapUpcomingForCarousel(upcomingTournaments ?? []),
    [upcomingTournaments],
  );
  const showCarousel = carouselTournaments.length > 0;

  if (!data || upcomingTournaments === null) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="home-page page-enter">
      <Navbar />

      {/* Hero — split: tournaments carousel + welcome */}
      <section className="home-hero hero-with-image section-geom" style={{ backgroundImage: `url(${HERO_IMAGE})` }}>
        <div className="hero-overlay" />
        <div className="hero-overlay-blend" />
        <div className={`container home-hero-split${showCarousel ? "" : " home-hero-split--solo"}`}>
          {showCarousel ? (
            <div className="home-hero-col home-hero-col--carousel">
              <TournamentHeroCarousel tournaments={carouselTournaments} />
            </div>
          ) : null}
          <div className="home-hero-col home-hero-col--content">
            {data.logo && (
              <div className="home-hero-logo-wrap">
                <img src={data.logo} alt="" className="home-hero-logo" />
              </div>
            )}
            <p className="home-hero-label">Welcome</p>
            <h1 className="home-hero-title">{data.organizationName}</h1>
            <h2 className="home-hero-headline">{data.heroSection.headline}</h2>
            <p className="home-hero-subtext">{data.heroSection.subText}</p>
            <div className="home-hero-strip">
              <a href="#impact" className="home-strip-item">
                <LogoTrophy className="strip-icon" aria-hidden />
                <span>Impact</span>
              </a>
              <span className="home-strip-dot" aria-hidden="true" />
              <a href="#programs" className="home-strip-item">
                <span>Programs</span>
              </a>
              <span className="home-strip-dot" aria-hidden="true" />
              <a href="#involved" className="home-strip-item">
                <LogoRocket className="strip-icon" aria-hidden />
                <span>Get involved</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Impact — asymmetric bento + typography */}
      <section id="impact" className="home-impact-bento">
        <div className="container home-impact-bento-inner">
          <p className="home-impact-eyebrow">Our impact</p>
          <div className="home-impact-layout">
            <div className="home-impact-hero card-reveal">
              <span className="home-impact-hero-number" aria-hidden="true">{data.impactMetrics.studentsTrained}+</span>
              <div className="home-impact-hero-content">
                <LogoUsers className="home-impact-hero-icon" aria-hidden />
                <span className="home-impact-hero-value">{data.impactMetrics.studentsTrained}+</span>
                <span className="home-impact-hero-label">Students trained</span>
              </div>
            </div>
            <div className="home-impact-strips">
              <div className="home-impact-strip-item card-reveal">
                <span className="home-impact-strip-bar" aria-hidden="true" />
                <span className="home-impact-strip-value">{data.impactMetrics.tournamentsOrganized}</span>
                <span className="home-impact-strip-label">Tournaments</span>
              </div>
              <div className="home-impact-strip-item card-reveal">
                <span className="home-impact-strip-bar" aria-hidden="true" />
                <span className="home-impact-strip-value">{data.impactMetrics.coachesAssociated}</span>
                <span className="home-impact-strip-label">Coaches</span>
              </div>
              <div className="home-impact-strip-item card-reveal">
                <span className="home-impact-strip-bar" aria-hidden="true" />
                <span className="home-impact-strip-value">{data.impactMetrics.yearsOfService}</span>
                <span className="home-impact-strip-label">Years</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="home-section home-vm">
        <div className="container home-vm-grid">
          <div className="home-vm-card card-reveal">
            <div className="home-vm-accent" aria-hidden="true" />
            <LogoTarget className="card-logo home-vm-logo" />
            <h2 className="home-block-title">Our Vision</h2>
            <p>{data.foundationOverview.vision}</p>
          </div>
          <div className="home-vm-card card-reveal">
            <div className="home-vm-accent" aria-hidden="true" />
            <LogoRocket className="card-logo home-vm-logo" />
            <h2 className="home-block-title">Our Mission</h2>
            <p>{data.foundationOverview.mission}</p>
          </div>
        </div>
      </section>

      {/* Core values — pills */}
      <section className="home-section home-values">
        <div className="container">
          <h2 className="home-section-title">Core values</h2>
          <div className="home-values-wrap">
            {data.values.map((value, i) => (
              <div key={i} className="home-value-pill card-reveal">
                <LogoMedal className="card-logo home-value-icon" />
                <span>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sports programs — bento: 1 featured + 3 */}
      <section id="programs" className="home-section home-programs">
        <div className="container">
          <h2 className="home-section-title">Sports programs</h2>
          <div className="home-programs-bento">
            <Link to="/about" className="home-program-featured card-reveal">
              <div
                className="home-program-featured-bg"
                style={{ backgroundImage: `url(${PROGRAM_IMAGES[0]})` }}
              >
                <div className="home-program-featured-overlay" aria-hidden="true" />
                <div className="home-program-featured-content">
                  <LogoTrophy className="card-logo home-program-icon" aria-hidden />
                  <h3>{data.sportsPrograms[0]?.name}</h3>
                  <p>{data.sportsPrograms[0]?.description}</p>
                  <span className="home-program-cta">Learn more →</span>
                </div>
              </div>
            </Link>
            <div className="home-program-list">
              {data.sportsPrograms.slice(1, 4).map((program, i) => (
                <Link key={i} to="/about" className="home-program-card card-reveal">
                  <div
                    className="home-program-card-bg"
                    style={{ backgroundImage: `url(${PROGRAM_IMAGES[(i + 1) % PROGRAM_IMAGES.length]})` }}
                  >
                    <div className="home-program-card-overlay" aria-hidden="true" />
                  </div>
                  <div className="home-program-card-content">
                    <h4>{program.name}</h4>
                    <p>{program.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About + Get involved */}
      <section id="involved" className="home-section home-about-involved">
        <div className="container home-ai-grid">
          <div className="home-about-card card-reveal">
            <div className="home-card-accent" aria-hidden="true" />
            <LogoBook className="card-logo home-about-logo" />
            <h2 className="home-block-title">About us</h2>
            <p className="home-about-desc">{data.foundationOverview.description}</p>
            <div className="home-focus-tags">
              {data.focusAreas.map((area, i) => (
                <span key={i} className="home-focus-tag">{area}</span>
              ))}
            </div>
            <Link to="/about" className="home-btn home-btn-outline">Discover more</Link>
          </div>
          <div className="home-involved-wrap">
            <h2 className="home-section-title">Get involved</h2>
            <div className="home-involved-grid">
              <Link to="/about" className="home-involved-card card-reveal">
                <LogoBook className="card-logo" />
                <h4>About</h4>
                <p>Learn more</p>
                <span className="home-involved-arrow">→</span>
              </Link>
              <Link to="/tournaments" className="home-involved-card card-reveal">
                <LogoTrophy className="card-logo" />
                <h4>Tournaments</h4>
                <p>View events</p>
                <span className="home-involved-arrow">→</span>
              </Link>
              <Link to="/contact" className="home-involved-card card-reveal">
                <LogoMail className="card-logo" />
                <h4>Contact</h4>
                <p>Get in touch</p>
                <span className="home-involved-arrow">→</span>
              </Link>
              <Link to="/donate" className="home-involved-card card-reveal">
                <LogoHeart className="card-logo" />
                <h4>Donate</h4>
                <p>Support us</p>
                <span className="home-involved-arrow">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Athlete development */}
      <section className="home-section home-athlete">
        <div className="container">
          <h2 className="home-section-title">Athlete development</h2>
          <div className="home-athlete-grid">
            {data.athleteDevelopment.programs.map((program, i) => (
              <div key={i} className="home-athlete-card card-reveal">
                <LogoTarget className="card-logo" />
                <span>{program}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community programs */}
      <section className="home-section home-community">
        <div className="container">
          <h2 className="home-section-title">Community programs</h2>
          <div className="home-community-grid">
            {data.communityPrograms.map((program, i) => (
              <div key={i} className="home-community-card card-reveal">
                <LogoUsers className="card-logo" />
                <span>{program}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="home-section home-cta">
        <div className="container">
          <div className="home-cta-card card-reveal">
            <div className="home-cta-accent" aria-hidden="true" />
            <LogoRocket className="card-logo home-cta-logo" aria-hidden />
            <h2 className="home-cta-title">{data.callToAction.title}</h2>
            <p className="home-cta-desc">{data.callToAction.description}</p>
            <div className="home-cta-btns">
              {data.callToAction.buttons.map((btn, i) => (
                btn.link.startsWith("#") ? (
                  <a key={i} href={btn.link} className="home-btn home-btn-primary">
                    {btn.label}
                  </a>
                ) : (
                  <Link key={i} to={btn.link} className="home-btn home-btn-primary">
                    {btn.label}
                  </Link>
                )
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer organizationName={data.organizationName} socialLinks={data.socialLinks} />
    </div>
  );
}

export default Home;
